import { Chip, Toolbar, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";
import ActionsCell from "../../../../../../common/ActionsCell";
import DataGrid from "../../../../../../common/DataGrid";
import { CreateButton, OrdinaryButton } from "../../../../../../common/Buttons";
import PaymentEditor from "./PaymentEditor";
import { ErrorIcon, PaymentPaidButton } from "../../../../../../common/icons/Icons";
import RestoreIcon from '@mui/icons-material/Restore';
import { dateUtils } from "../../../../../../utils/DateUtils";

const ENUM_PAYMENT_STATUS = {
    PENDING: {
        label: 'Pending',
        color: 'info'
    },
    OVERDUE: {
        label: 'Overdue',
        color: 'error'
    },
    PAID: {
        label: 'Paid',
        color: 'success'
    }
}

const PaymentStatus = ({ status }) => {
    return <Chip color={status.color} label={status.label} sx={{ margin: '0px 0px', width: '75px' }} />
}

const PaymentsList = ({ propertyId, payments, reservation, editable, expectedTotals, onCreated, onUpdated, onDeleted, onReset, disableResetButton }) => {
    let [editorOpen, setEditorOpen] = useState(false);
    let [editedPayment, setEditedPayment] = useState(null);

    const onAdd = () => {
        setEditedPayment(null);
        setEditorOpen(true);
    };

    const onCreateSuccess = (payment) => {
        setEditorOpen(false);

        onCreated(payment);
    }

    const onUpdateSuccess = (payment) => {
        setEditorOpen(false);

        onUpdated(payment);
    }

    const openEditor = () => {
        setEditorOpen(true);
    }

    const closeEditor = () => {
        setEditorOpen(false);
    }

    const onDelete = (paymentId) => {
        onDeleted(paymentId);
    }

    const onEdit = (paymentId) => {
        const payment = payments.find(payment => payment.id === paymentId);

        setEditedPayment(payment);
        openEditor();
    }

    const onMarkAsPaid = (paymentId) => {
        const updatedPayment = payments.find(payment => payment.id === paymentId);

        onUpdated({
            ...updatedPayment,
            paidDate: new Date(),
            paymentMethod: 'TRANSFER',
        });
    }

    const dateComparator = (v1, v2) => {
        // move total to the end
        if (!v1) {
            return 1;
        }
        return v1 - v2;
    }

    if (!payments) {
        return null;
    }

    const gridColumns = [
        {
            field: 'id',
            headerName: 'ID',
            disableColumnMenu: true,
            sortable: false,
            hide: true
        },
        {
            field: 'dueDate',
            headerName: 'Due date',
            minWidth: 200,
            flex: 1,
            sortable: false,
            disableColumnMenu: true,
            sortComparator: dateComparator,
            renderCell: (params) => dateUtils.getFormattedDate(params.value)
        },
        {
            field: 'paidDate',
            headerName: 'Paid date',
            minWidth: 200,
            flex: 1,
            sortable: false,
            disableColumnMenu: true,
            valueGetter: (params) => dateUtils.getFormattedDate(params.row.paidDate),
        },
        {
            field: 'paymentStatus',
            headerName: 'Status',
            disableColumnMenu: true,
            sortable: false,
            width: 100,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                let status;

                const { id, dueDate, paidDate } = params.row;

                if (id === 'total') {
                    return null;
                }

                if (paidDate) {
                    status = ENUM_PAYMENT_STATUS.PAID;
                }
                else {
                    const now = new Date();
                    dueDate.setHours(23, 59, 59, 0);

                    if (dueDate < now) {
                        status = ENUM_PAYMENT_STATUS.OVERDUE;
                    }
                    else {
                        status = ENUM_PAYMENT_STATUS.PENDING;
                    }
                }

                return <PaymentStatus status={status} />;
            }
        },
        {
            field: 'amountDue',
            headerName: 'Amount',
            disableColumnMenu: true,
            sortable: false,
            type: 'number',
            valueFormatter: (params) => {
                return `PLN ${params.value}`;
            },
            renderCell: (params) => {
                if (params.id === 'total' && expectedTotals?.amountDue && params.value !== expectedTotals.amountDue) {
                    return <>
                        <ErrorIcon
                            title={`The sum of all payments is different than the total reservation price`}
                            sx={{
                                margin: '5px',
                            }}
                        />
                        {params.formattedValue}
                    </>;
                }
                else return params.formattedValue;
            },
            cellClassName: (params) => {
                if (params.id === 'total' && expectedTotals?.amountDue && params.value !== expectedTotals.amountDue) {
                    return 'error';
                }
            },
        },
        {
            field: 'actions',
            headerName: '',
            minWidth: 100,
            flex: 1,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params) => {
                if (params.row.id === 'total') {
                    return null;
                }

                return editable && <ActionsCell
                    entityId={params.row.id}
                    onDeleteClick={onDelete}
                    onEditClick={onEdit}
                    customActions={[
                        {
                            id: 'markAsPaid',
                            position: 15,
                            icon: PaymentPaidButton,
                            onClick: onMarkAsPaid,
                            disabled: !!params.row.paidDate
                        }
                    ]}
                />
            }
        }
    ];

    const totalAmountDue = payments.reduce((acc, payment) => acc + payment.amountDue, 0);

    const rows = [...payments, { id: 'total', amountDue: totalAmountDue }];

    let paymentsList = null;

    if (!payments.length) {
        paymentsList = <>
            <Typography
                variant="h6"
                component="div"
                sx={{ margin: '20px', textAlign: 'center' }}
            >
                There are no payments associated with this reservation
            </Typography>
            <CreateButton sx={{ width: '300px', height: '50px', alignSelf: 'center' }} onClick={onAdd} text='Add' />
        </>;
    }
    else {
        paymentsList = <>
            <Toolbar sx={{ gap: '10px', padding: '0px !important' }}>
                <Typography component="div" sx={{ fontWeight: 700 }}>Payments</Typography>
                <Box sx={{ flexGrow: 1 }} />
                {editable && <CreateButton onClick={onAdd} text='Add Payment' />}
                {editable && <OrdinaryButton onClick={onReset} text='Reset Payments' disabled={disableResetButton} icon={<RestoreIcon sx={{ paddingRight: '5px' }} />} />}
            </Toolbar>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <DataGrid
                    rows={rows}
                    columns={gridColumns}
                    disableSelectionOnClick
                    hideFooter
                    autoHeight
                    sx={{
                        margin: '0px !important',
                        "& .MuiDataGrid-row--total": {
                            backgroundColor: "#f0f0f0",
                            fontWeight: "bold"
                        },
                        '& .error': {
                            color: 'error.main',
                        },
                    }}
                    getRowClassName={(params) =>
                        params.id === "total" ? "MuiDataGrid-row--total" : ""
                    }
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'dueDate', sort: 'asc' }],
                        },
                    }}
                />
            </Box>
        </>
    }

    return <>
        {paymentsList}
        {editorOpen && <PaymentEditor
            onSuccess={editedPayment ? onUpdateSuccess : onCreateSuccess}
            onClose={closeEditor}
            editedPayment={editedPayment}
            existingPayments={payments}
            defaultAmountDue={Math.max(reservation.totalPrice - totalAmountDue, 0)}
            defaultDueDate={reservation.checkin}
        />}
    </>;
}

export default PaymentsList;